import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import "./Plan.css"; // Import the animation and glass style

const planIntroContent = {
  onGrid: {
    hero: {
      title: "What is an On-Grid Solar System?",
      subtitle:
        "Connected to the electricity grid. Cost-effective, efficient, and widely adopted in urban areas.",
      image: "/images/plan/onGrid-hero.jpg",
    },
    benefits: [
      {
        title: "Government Subsidy Eligible",
        description: "Claim up to 40% subsidy under the national rooftop solar program.",
      },
      {
        title: "Lower Electricity Bills",
        description: "Offset energy usage and reduce monthly costs by exporting excess power to the grid.",
      },
      {
        title: "No Battery Required",
        description: "Reduces cost and maintenance by eliminating battery dependency.",
      },
    ],
    stages: [
      {
        label: "Stage 1: Consumption & Site Assessment",
        description: "We’ll ask about your average bill, rooftop area, and location.",
      },
      {
        label: "Stage 2: Installation Details",
        description: "Choose components based on your preferences and budget.",
      },
      {
        label: "Stage 3: Final Steps",
        description: "Download brochure, request callback, or get a quotation.",
      },
    ],
    callToAction: {
      buttonLabel: "Start Planning On-Grid",
    },
  },
  offGrid: {
    hero: {
      title: "What is an Off-Grid Solar System?",
      subtitle: "Completely independent from the electricity grid — ideal for remote or rural locations.",
      image: "/images/plan/offGrid-hero.jpg",
    },
    benefits: [
      {
        title: "Freedom from Electricity Bills",
        description: "Self-generate all your power and avoid rising utility rates permanently.",
      },
      {
        title: "Battery Backup Included",
        description: "Stay powered even during outages with built-in battery storage.",
      },
      {
        title: "Works Without Grid Access",
        description: "Perfect for rural homes, farms, or remote properties.",
      },
    ],
    stages: [
      {
        label: "Stage 1: Needs Assessment",
        description: "We collect your usage & space info to size the system.",
      },
      {
        label: "Stage 2: Equipment Selection",
        description: "Choose top-tier solar panels, batteries, and more.",
      },
      {
        label: "Stage 3: Connect With Us",
        description: "Download brochure or request a quotation.",
      },
    ],
    callToAction: {
      buttonLabel: "Start Planning Off-Grid",
    },
  },
};

const PlanIntro = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const planKey = location.pathname.includes("offGrid") ? "offGrid" : "onGrid";
  const content = planIntroContent[planKey];

  return (
    <Box px={2} maxWidth="1200px" mx="auto">
      {/* Hero Section */}
      <Box textAlign="center" mb={5}>
        <Typography className="fade-in" variant="h5" fontWeight={600} gutterBottom>
          {content.hero.title}
        </Typography>
        <Typography
          className="fade-in"
          variant="body1"
          color="text.secondary"
          maxWidth="700px"
          mx="auto"
        >
          {content.hero.subtitle}
        </Typography>
        {/* <Box
          component="img"
          src={content.hero.image}
          alt="plan type"
          width="100%"
          maxHeight={320}
          sx={{ borderRadius: 3, objectFit: "cover", mt: 3 }}
          className="image-fade-in"
        /> */}
      </Box>

      {/* Two-Column Content */}
      <Grid container spacing={4}>
        {/* Benefits */}
         <Grid item xs={12} md={6} sx={{ flex: 1 }}>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{textAlign:"center"}}>
            Key Benefits
          </Typography>
          {content.benefits.map(({ title, description }, idx) => (
            <Box key={idx} className="glass-card slide-in-left" mb={2}>
              <Typography variant="body1" fontWeight="bold" sx={{textAlign:"center"}}>{title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{textAlign:"center"}}>{description}</Typography>
            </Box>
          ))}
        </Grid>

        {/* Stages */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{textAlign:"center"}}>
            What We’ll Cover
          </Typography>
          {content.stages.map(({ label, description }, idx) => (
            <Box key={idx} className="glass-card slide-in-right" mb={2}>
              <Typography variant="body1" fontWeight="bold">{label}</Typography>
              <Typography variant="body2" color="text.secondary">{description}</Typography>
            </Box>
          ))}
        </Grid>
      </Grid>

      {/* CTA */}
      <Box mt={5} textAlign="center">
        <Button
          className="pulse-button"
          variant="contained"
          size="large"
          onClick={() => navigate("stage1")}
        >
          {content.callToAction.buttonLabel}
        </Button>
      </Box>
    </Box>
  );
};

export default PlanIntro;
